import { Link, Navigate } from "react-router-dom";
import { getDecodedToken } from "../auth";
import { useEffect, useState } from "react";
import { server } from "../server";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { fetchDuesFeeDetails } from "../redux/duesFeeSlice";

function Home() {
  const user = getDecodedToken();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [homework, setHomework] = useState([]);
  const [schoolAttendanceStats, setSchoolAttendanceStats] = useState({
    total: 0,
    present: 0,
    absent: 0,
  });
  const [staffAttendanceOverall, setStaffAttendanceOverall] = useState({
    total: 0,
    present: 0,
    absent: 0,
  });
  const [staffAttendanceByCategory, setStaffAttendanceByCategory] = useState(
    {},
  );
  const dispatch = useDispatch();
  const { data: duesFeeDetails } = useSelector((state) => state.duesFee);
  const userRole = user?.roles;
  const userId = user?.userId;

  const fetchHomeworkDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${server}/all_homework_of_currentDate_or_byDate`,
        {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        },
      );
      const responseData = response.data?.["body-json"]?.body?.data;
      if (Array.isArray(responseData)) {
        const sortedData = responseData?.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        setHomework(sortedData);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching fee details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeworkDetails();
  }, []);

  // ── School Attendance Snapshot (mirrors SchoolAttendance.js) ────────────
  const fetchSchoolAttendanceSnapshot = async () => {
    try {
      const todayDate = new Date().toISOString().split("T")[0];
      const response = await axios.post(
        `${server}/student_attendance_of_school_bydate_mongodb`,
        { date: todayDate },
        {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        },
      );

      const parsedBody = JSON.parse(response.data["body-json"]?.body || "{}");
      const attendanceData = parsedBody.schoolAttendance;

      if (Array.isArray(attendanceData)) {
        const present = attendanceData.filter(
          (student) => student.school?.status === "Present",
        ).length;
        const absent = attendanceData.filter(
          (student) => student.school?.status === "Absent",
        ).length;

        setSchoolAttendanceStats({
          total: attendanceData.length,
          present,
          absent,
        });
      }
    } catch (error) {
      console.error("Error fetching school attendance snapshot:", error);
    }
  };

  // ── Staff Attendance Snapshot, category-wise (mirrors StaffAttendance.js) ─
  const fetchStaffAttendanceSnapshot = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const response = await axios.post(
        `${server}/staffAttendanceByDate`,
        { date: today },
        {
          headers: { Authorization: `${localStorage.getItem("token")}` },
        },
      );

      const apiData = response.data?.["body-json"];
      if (apiData?.statusCode !== 200) return;

      let parsedBody = apiData.body;
      if (typeof parsedBody === "string") {
        try {
          parsedBody = JSON.parse(parsedBody);
        } catch {
          parsedBody = {};
        }
      }

      const attendanceList = Array.isArray(parsedBody?.data)
        ? parsedBody.data
        : [];

      // Build category-wise totals
      const categoryMap = {};
      attendanceList.forEach((staff) => {
        const category = staff?.categoryDetails?.staffCategory || "Unspecified";
        const isPresent = staff?.attendance?.in?.status === "Present";

        if (!categoryMap[category]) {
          categoryMap[category] = { total: 0, present: 0, absent: 0 };
        }
        categoryMap[category].total += 1;
        if (isPresent) {
          categoryMap[category].present += 1;
        } else {
          categoryMap[category].absent += 1;
        }
      });
      setStaffAttendanceByCategory(categoryMap);

      // Overall totals — prefer backend summary if present, else derive
      if (parsedBody?.summary) {
        setStaffAttendanceOverall({
          total: parsedBody.summary.total || 0,
          present: parsedBody.summary.present || 0,
          absent: parsedBody.summary.absent || 0,
        });
      } else {
        const total = attendanceList.length;
        const present = attendanceList.filter(
          (s) => s?.attendance?.in?.status === "Present",
        ).length;
        setStaffAttendanceOverall({ total, present, absent: total - present });
      }
    } catch (error) {
      console.error("Error fetching staff attendance snapshot:", error);
    }
  };

  useEffect(() => {
    fetchSchoolAttendanceSnapshot();
    fetchStaffAttendanceSnapshot();
  }, []);

  useEffect(() => {
    fetch(`${server}/DashboardStats`, {
      headers: {
        Authorization: `${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => setStats(data))
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    dispatch(fetchDuesFeeDetails());
  }, [dispatch]);

  // Calculate totals
  let totalMonthlyDue = 0;
  let totalTransportDue = 0;

  duesFeeDetails.forEach((student) => {
    const unpaidMonths = student.unpaidMonths || [];
    const monthlyFee = student.monthlyFee || 0;
    const transportFee = student.transportFee || 0;

    const monthCount = unpaidMonths.length;
    const transportableMonths = unpaidMonths.filter((m) => m !== "June").length;

    totalMonthlyDue += monthlyFee * monthCount;
    totalTransportDue += transportFee * transportableMonths;
  });

  const totalDueAllStudents = totalMonthlyDue + totalTransportDue;

  if (!user) return <Navigate to="/" replace />;

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-12">
                  <div className="row g-2">
                    {(userRole?.includes("Master Admin") ||
                      userRole?.includes("Admin")) && (
                      <>
                        <div className="col-lg-4 col-12">
                          <div className="row g-2 h-100">
                            <div className="col-6 h-100">
                              <div className="bg-dark-blue p-4 rounded-4 h-100">
                                <div className="mb-2">
                                  <h3 className="text-white lh-1 m-0">
                                    {stats?.totalStudents}
                                  </h3>
                                  <p className="text-light fs-6">
                                    Active Students
                                  </p>
                                  <div class="avatar-group mt-2">
                                    {[1, 2, 3, 4, 5].map((num) => (
                                      <span
                                        className="avatar avatar-sm"
                                        key={num}
                                      >
                                        <img
                                          alt={`avatar-${num}`}
                                          src={`/assets/images/students/${num}.jpg`}
                                          className="rounded-circle"
                                        />
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <div className="pt-6">
                                  <p className="text-light mb-1 fs-6">
                                    {stats?.deactivatedStudents} Inactive
                                  </p>
                                  <div
                                    class="progress"
                                    role="progressbar"
                                    aria-label="Basic example"
                                    aria-valuenow="75"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  >
                                    <div class="progress-bar w-75 bg-danger"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-6 h-100">
                              <div className="bg-white p-4 rounded-4 h-100">
                                <div className="mb-7">
                                  <h3 className="text-black lh-1 m-0">
                                    {stats?.totalStaffs}
                                  </h3>
                                  <p className="text-dark opacity-75 fs-6">
                                    Active Staffs
                                  </p>
                                </div>
                                <div>
                                  <p className="text-dark mb-1 fs-6">
                                    {stats?.leftStaffs} Left
                                  </p>
                                  <div class="avatar-group mt-2">
                                    {[1, 2, 3, 4, 5].map((num) => (
                                      <span
                                        className="avatar avatar-sm"
                                        key={num}
                                      >
                                        <img
                                          alt={`avatar-${num}`}
                                          src={`/assets/images/students/${num}.jpg`}
                                          className="rounded-circle"
                                        />
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-12">
                          <div className="bg-white rounded-4 h-100 overflow-hidden">
                            <div className="bg-light-green z-1 position-relative p-4">
                              <div className="d-flex justify-content-between align-items-start pb-3">
                                <div>
                                  <h3 className="text-white lh-1 m-0">
                                    <span className="fs-5">₹</span>
                                    {totalDueAllStudents.toLocaleString(
                                      "en-IN",
                                    )}
                                  </h3>
                                  <p className="text-light fs-6">
                                    Record of Fees Due as of June
                                  </p>
                                </div>
                                <Link
                                  to="/dues-fee"
                                  class="badge bg-light-soft"
                                >
                                  Report
                                </Link>
                              </div>
                            </div>
                            <div className="bg-light row g-1 mt-2 px-4 pb-4 mt-n6">
                              <div className="col-6">
                                <div className="rounded-4 bg-white px-2 py-4 lh-sm z-2 position-relative">
                                  <div className="d-flex flex-column lh-sm text-dark text-center">
                                    <i className="bi bi-wallet2 fs-3 mb-1"></i>
                                    <p className="text-dark fs-6 opacity-75 mb-0">
                                      Monthly Fee
                                    </p>
                                    <span class="text-dark fw-medium">
                                      <span className="fs-7">₹</span>
                                      {totalMonthlyDue.toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="rounded-4 bg-white px-2 py-4 lh-sm z-2 position-relative">
                                  <div className="d-flex flex-column lh-sm text-dark text-center">
                                    <i className="bi bi-bus-front fs-3 mb-1"></i>
                                    <p className="text-dark fs-6 opacity-75 mb-0">
                                      Transport Fee
                                    </p>
                                    <span class="text-dark fw-medium">
                                      <span className="fs-7">₹</span>
                                      {totalTransportDue.toLocaleString(
                                        "en-IN",
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-12">
                          <div className="bg-light-pink p-4 rounded-4 h-100">
                            <div className="mb-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <p className="text-dark fs-6 opacity-75 mb-0">
                                    Today's Collection
                                  </p>
                                  <h3 className="text-black lh-1 mt-0 mb-1">
                                    <span className="fs-5">₹</span>
                                    {stats?.todaysCollection.toLocaleString()}
                                  </h3>
                                </div>
                                <span class="badge bg-dark-soft">DCR</span>
                              </div>

                              <p
                                className={`fw-semibold mb-0 ${
                                  stats?.todaysCollection >=
                                  stats?.yesterdaysCollection
                                    ? "text-success"
                                    : "text-danger"
                                }`}
                              >
                                <i
                                  className={`bi ${
                                    stats?.todaysCollection >
                                    stats?.yesterdaysCollection
                                      ? "bi-graph-up-arrow"
                                      : stats?.todaysCollection <
                                          stats?.yesterdaysCollection
                                        ? "bi-graph-down-arrow"
                                        : "bi-graph-up"
                                  } me-1`}
                                ></i>
                                {Math.abs(
                                  (
                                    ((stats?.todaysCollection -
                                      stats?.yesterdaysCollection) /
                                      stats?.yesterdaysCollection) *
                                    100
                                  ).toFixed(1),
                                )}
                                %
                                <span className="text-dark fs-5 fw-normal ms-2">
                                  from yesterday
                                </span>
                              </p>

                              <Link
                                to="/monthly-fees"
                                class="btn btn-primary btn-sm rounded-3 px-4 mt-6 fw-normal"
                              >
                                Collection History
                              </Link>
                            </div>
                            <div className="mt-n8 d-flex float-end">
                              <img src="/assets/images/wallet.png" width="90" />
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-12">
                          <div className="bg-white rounded-4 h-100">
                            <div className="col-12">
                              <div className="bg-yellow-gradient p-4 rounded-4 mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-dark fs-6 opacity-75 mb-0">
                                      {new Date().toLocaleDateString("en-GB")}
                                    </p>
                                    <h5 className="text-dark fs-5 fw-medium mb-0 lh-sm">
                                      Homework Assigned
                                    </h5>
                                    <h3 className="text-black lh-1 mt-0 mb-1">
                                      <i className="bi bi-card-heading me-1"></i>{" "}
                                      {homework.length}{" "}
                                      <span className="text-dark fs-6 fw-normal opacity-75">
                                        Homework
                                      </span>
                                    </h3>
                                  </div>
                                  <Link
                                    to="/homework"
                                    class="badge bg-dark-soft"
                                  >
                                    Report
                                  </Link>
                                </div>
                              </div>
                            </div>

                            <div className="p-4 pt-3">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h4 className="text-black lh-1 m-0">
                                    Quick Links
                                  </h4>
                                  <p className="text-dark fs-6 opacity-75 m-0">
                                    Easily navigate to pages
                                  </p>
                                </div>
                                <Link
                                  to="/user-details"
                                  class="badge bg-dark-soft"
                                >
                                  All Users
                                </Link>
                              </div>

                              <div className="row g-1 mt-2 pt-1">
                                <div className="col-5">
                                  <Link to="/notice-board">
                                    <div className="px-2 pt-4 rounded-4 text-center bg-light-5 h-100 d-flex flex-column justify-content-between align-items-center">
                                      <div>
                                        <h5 className="text-black lh-1 m-0">
                                          Notice Board
                                        </h5>
                                        <p className="text-dark fs-6 opacity-75 m-0">
                                          View Latest Notices
                                        </p>
                                      </div>

                                      <img
                                        src="/assets/images/sent.png"
                                        width="90"
                                      />
                                    </div>
                                  </Link>
                                </div>
                                <div className="col-7">
                                  <div className="row g-1 h-100">
                                    <div className="col-12">
                                      <Link to="/monthly-fees">
                                        <div className="px-2 py-4 rounded-4 text-center bg-light-3">
                                          <h5 className="text-black lh-1 m-0">
                                            Monthly Fees
                                          </h5>
                                          <p className="text-dark fs-6 opacity-75 m-0 lh-sm mt-1">
                                            Check Fee Collection
                                          </p>
                                        </div>
                                      </Link>
                                    </div>
                                    <div className="col-12">
                                      <Link to="/staff-directory">
                                        <div className="px-2 py-3 rounded-4 text-center bg-light-4">
                                          <h5 className="text-black lh-1 m-0">
                                            Staff Directory
                                          </h5>
                                          <p className="text-dark fs-6 opacity-75 m-0">
                                            Browse Staff Records
                                          </p>
                                        </div>
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-12 pt-1">
                                <Link to="/student-details">
                                  <div className="px-2 py-3 rounded-4 text-center bg-light-2">
                                    <h5 className="text-black lh-1 m-0">
                                      Student Details
                                    </h5>
                                    <p className="text-dark fs-6 opacity-75 m-0">
                                      Browse Student Records
                                    </p>
                                  </div>
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-12">
                          <div className="h-100 bg-white rounded-4">
                            <div className="bg-dark p-4 rounded-4 mb-2">
                              <div className="mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <p className="text-light fs-6 opacity-75 mb-0">
                                      {new Date().toLocaleDateString("en-GB")}
                                    </p>
                                    <h5 className="text-white fs-5 fw-medium mb-0">
                                      Online Collection
                                    </h5>
                                  </div>
                                  <span class="badge bg-light-soft">
                                    Report
                                  </span>
                                </div>
                                <h3 className="text-white lh-1 mt-0 mb-1">
                                  <span className="fs-5">₹</span>
                                  {stats?.todaysOnlineCollection.toLocaleString()}
                                </h3>
                                <p
                                  className={`fw-semibold mb-0 mt-3 ${
                                    stats?.todaysOnlineCollection >=
                                    stats?.yesterdaysOnlineCollection
                                      ? "text-success"
                                      : "text-danger"
                                  }`}
                                >
                                  <i
                                    className={`bi ${
                                      stats?.todaysOnlineCollection >
                                      stats?.yesterdaysOnlineCollection
                                        ? "bi-graph-up-arrow"
                                        : stats?.todaysOnlineCollection <
                                            stats?.yesterdaysOnlineCollection
                                          ? "bi-graph-down-arrow"
                                          : "bi-graph-up"
                                    } me-1`}
                                  ></i>
                                  {Math.abs(
                                    (
                                      ((stats?.todaysOnlineCollection -
                                        stats?.yesterdaysOnlineCollection) /
                                        stats?.yesterdaysOnlineCollection) *
                                      100
                                    ).toFixed(1),
                                  )}
                                  %
                                  <span className="text-light fs-5 fw-normal ms-2">
                                    from yesterday
                                  </span>
                                </p>
                              </div>
                            </div>

                            <div className="bg-white p-4 rounded-4">
                              <div className="mb-4 text-center">
                                <p className="text-dark fs-5 mb-1">
                                  Manage Password
                                </p>
                                <h3 className="text-black lh-1 m-0 px-2 fw-medium fs-4">
                                  Have you updated your password this month ?
                                </h3>
                                <p className="text-dark opacity-75 fs-6">
                                  For Security Reasons
                                </p>
                              </div>
                              <div className="d-flex justify-content-center align-items-center">
                                <img
                                  src="/assets/images/password.svg"
                                  className="w-50"
                                />
                                <Link
                                  to="/settings"
                                  class="btn btn-success btn-sm rounded-3 px-4 fw-normal mt-4"
                                >
                                  Try Now
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-12">
                          <div className="bg-blue-gradient p-4 rounded-4 h-100">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <p className="text-white lh-sm fs-5 mb-3">
                                  Today's Attendance
                                </p>
                                <p className="text-white fs-5 opacity-75 m-0 lh-sm">
                                  School & Bus Attendance Statistics
                                </p>
                                <h4 className="text-white lh-1 m-0">
                                  Attendance Analytics
                                </h4>
                              </div>
                              <span class="badge bg-white-soft">Report</span>
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                              <div className="mt-3">
                                <div className="d-flex align-items-center mb-2">
                                  <span class="avatar avatar-md">
                                    <span class="avatar-initials rounded-circle bg-transparent-light">
                                      <i className="bi bi-bank text-white"></i>
                                    </span>
                                  </span>
                                  <div className="ms-2">
                                    <p className="text-white fs-6 opacity-75 mb-0 lh-1">
                                      School
                                    </p>
                                    <span class="text-white fw-medium m-0 lh-1">
                                      {stats?.todaysAttendance?.school?.present}
                                      /{stats?.todaysAttendance?.school?.absent}
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                  <span class="avatar avatar-md">
                                    <span class="avatar-initials rounded-circle bg-transparent-light">
                                      <i className="bi bi-bus-front text-white"></i>
                                    </span>
                                  </span>
                                  <div className="ms-2">
                                    <p className="text-white fs-6 opacity-75 mb-0 lh-1">
                                      Bus PickUp
                                    </p>
                                    <span class="text-white fw-medium m-0 lh-1">
                                      {stats?.todaysAttendance?.pickup?.present}
                                      /{stats?.todaysAttendance?.pickup?.absent}
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                  <span class="avatar avatar-md">
                                    <span class="avatar-initials rounded-circle bg-transparent-light">
                                      <i className="bi bi-bus-front text-white"></i>
                                    </span>
                                  </span>
                                  <div className="ms-2">
                                    <p className="text-white fs-6 opacity-75 mb-0 lh-1">
                                      Bus Drop
                                    </p>
                                    <span class="text-white fw-medium m-0 lh-1">
                                      {stats?.todaysAttendance?.drop?.present}/
                                      {stats?.todaysAttendance?.drop?.absent}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <img
                                src="/assets/images/attendance.svg"
                                width="140"
                              />
                            </div>

                            <p className="text-white fs-5 opacity-75 m-0 lh-sm">
                              Showing Present / Absent
                            </p>

                            <p className="text-white lh-sm fs-5 mt-5">
                              Comprehensive Daily Attendance Summary &mdash;
                              School and Transportation Statistics.
                            </p>
                            <div className="mt-5">
                              <Link
                                to="/school-attendance-statistics"
                                class="btn bg-dark text-white btn-sm rounded-3 px-4 fw-normal me-2"
                              >
                                School Statistics
                              </Link>
                              <Link
                                to="/bus-attendance-statistics"
                                class="bg-transparent-light px-4 py-2 text-white btn-sm rounded-3 fs-6 fw-normal"
                              >
                                Bus Statistics
                              </Link>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-4 col-12">
                          <div className="bg-white rounded-4 h-100 overflow-hidden">
                            <div className="bg-dark-blue p-4 rounded-4">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                  <p className="text-light fs-6 opacity-75 mb-0">
                                    {new Date().toLocaleDateString("en-GB")}
                                  </p>
                                  <h5 className="text-white fs-5 fw-medium mb-0 lh-sm">
                                    Attendance Snapshot
                                  </h5>
                                </div>
                                <Link
                                  to="/school-attendance-statistics"
                                  class="badge bg-light-soft"
                                >
                                  Report
                                </Link>
                              </div>

                              <p className="text-light fs-6 opacity-75 mb-2">
                                Student's Attendance
                              </p>
                              <div className="row g-1">
                                <div className="col-4">
                                  <div className="rounded-4 bg-white px-2 py-3 text-center h-100">
                                    <i className="bi bi-people fs-4 text-secondary"></i>
                                    <h5 className="text-black lh-1 m-0 mt-1">
                                      {schoolAttendanceStats.total}
                                    </h5>
                                    <p className="text-dark fs-7 opacity-75 m-0">
                                      Strength
                                    </p>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="rounded-4 bg-white px-2 py-3 text-center h-100">
                                    <i className="bi bi-check-circle fs-4 text-success"></i>
                                    <h5 className="text-black lh-1 m-0 mt-1">
                                      {schoolAttendanceStats.present}
                                    </h5>
                                    <p className="text-dark fs-7 opacity-75 m-0">
                                      Present
                                    </p>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="rounded-4 bg-white px-2 py-3 text-center h-100">
                                    <i className="bi bi-x-circle fs-4 text-danger"></i>
                                    <h5 className="text-black lh-1 m-0 mt-1">
                                      {schoolAttendanceStats.absent}
                                    </h5>
                                    <p className="text-dark fs-7 opacity-75 m-0">
                                      Absent
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="px-4 py-3">
                              <p className="text-dark fs-6 opacity-75 mb-2">
                                Staff's Attendance
                              </p>
                              <div className="row g-1 mb-3">
                                <div className="col-4">
                                  <div className="rounded-4 bg-light px-2 py-3 text-center h-100">
                                    <i className="bi bi-people fs-4 text-secondary"></i>
                                    <h5 className="text-black lh-1 m-0 mt-1">
                                      {staffAttendanceOverall.total}
                                    </h5>
                                    <p className="text-dark fs-7 opacity-75 m-0">
                                      Strength
                                    </p>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="rounded-4 bg-success-soft px-2 py-3 text-center h-100">
                                    <i className="bi bi-people fs-4 text-secondary"></i>
                                    <h5 className="text-black lh-1 m-0 mt-1">
                                      {staffAttendanceOverall.present}
                                    </h5>
                                    <p className="text-dark fs-7 opacity-75 m-0">
                                      Present
                                    </p>
                                  </div>
                                </div>
                                <div className="col-4">
                                  <div className="rounded-4 bg-danger-soft px-2 py-3 text-center h-100">
                                    <i className="bi bi-people fs-4 text-secondary"></i>
                                    <h5 className="text-black lh-1 m-0 mt-1">
                                      {staffAttendanceOverall.absent}
                                    </h5>
                                    <p className="text-dark fs-7 opacity-75 m-0">
                                      Absent
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {Object.entries(staffAttendanceByCategory).map(
                                ([category, data]) => {
                                  const percent =
                                    data.total > 0
                                      ? Math.round(
                                          (data.present / data.total) * 100,
                                        )
                                      : 0;
                                  return (
                                    <div key={category} className="mb-2 pb-1">
                                      <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="fs-6 fw-medium text-dark">
                                          {category}
                                        </span>
                                        <span className="fs-6 text-secondary">
                                          {data.present}/{data.total}
                                        </span>
                                      </div>
                                      <div
                                        className="progress"
                                        style={{ height: "6px" }}
                                      >
                                        <div
                                          className="progress-bar bg-success"
                                          style={{ width: `${percent}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="col-lg-5 col-12">
                      <div className="bg-white p-4 rounded-4 h-100">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h4 className="text-black lh-1 m-0">
                              What’s up Today
                            </h4>
                            <p className="text-dark fs-6 opacity-75 m-0">
                              School Calender / Planner
                            </p>
                          </div>
                          <span class="badge bg-dark-soft">Report</span>
                        </div>

                        <div className="my-3">
                          <div className="row g-1">
                            <div className="col">
                              <div className="rounded-pill bg-red py-3 lh-sm calendar-pill">
                                <p className="text-white text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal">Su</span>
                                  <br />
                                  20
                                </p>
                              </div>
                            </div>
                            <div className="col">
                              <div className="border border-light rounded-pill py-3 lh-sm calendar-pill">
                                <p className="text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal opacity-75">
                                    Mo
                                  </span>
                                  <br />
                                  21
                                </p>
                              </div>
                            </div>
                            <div className="col">
                              <div className="border border-light rounded-pill py-3 lh-sm calendar-pill">
                                <p className="text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal opacity-75">
                                    Tu
                                  </span>
                                  <br />
                                  22
                                </p>
                              </div>
                            </div>
                            <div className="col">
                              <div className="border border-light rounded-pill py-3 lh-sm calendar-pill">
                                <p className="text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal opacity-75">
                                    We
                                  </span>
                                  <br />
                                  23
                                </p>
                              </div>
                            </div>
                            <div className="col">
                              <div className="border border-light rounded-pill py-3 lh-sm calendar-pill">
                                <p className="text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal opacity-75">
                                    Th
                                  </span>
                                  <br />
                                  24
                                </p>
                              </div>
                            </div>
                            <div className="col">
                              <div className="border border-light rounded-pill py-3 lh-sm calendar-pill">
                                <p className="text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal opacity-75">
                                    Fr
                                  </span>
                                  <br />
                                  25
                                </p>
                              </div>
                            </div>
                            <div className="col">
                              <div className="border border-light rounded-pill py-3 lh-sm calendar-pill">
                                <p className="text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal opacity-75">
                                    Sa
                                  </span>
                                  <br />
                                  27
                                </p>
                              </div>
                            </div>
                            <div className="col">
                              <div className="border border-light rounded-pill py-3 lh-sm calendar-pill">
                                <p className="text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal opacity-75">
                                    Su
                                  </span>
                                  <br />
                                  28
                                </p>
                              </div>
                            </div>
                            <div className="col">
                              <div className="border border-light rounded-pill py-3 lh-sm calendar-pill">
                                <p className="text-center fs-5 fw-semibold m-0">
                                  <span className="fs-6 fw-normal opacity-75">
                                    Mo
                                  </span>
                                  <br />
                                  29
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-3">
                          <div className="d-flex bg-light align-items-center rounded-3 mb-1 py-1 overflow-hidden">
                            <div className="py-5 bg-purple px-sm1 rounded-pill"></div>
                            <div className="ms-3">
                              <p className="text-dark fs-6 mb-0">
                                20/06/2025 &mdash; Sunday
                              </p>
                              <h5 className="text-black lh-1 mt-0 mb-1">
                                Save Forest Save Earth (Speech)
                              </h5>
                              <p className="text-dark fs-6 opacity-75 mb-0">
                                Event
                              </p>
                            </div>
                          </div>
                          <div className="d-flex bg-light align-items-center rounded-3 mb-2 py-1 overflow-hidden">
                            <div className="py-5 bg-danger px-sm1 rounded-pill"></div>
                            <div className="ms-3">
                              <p className="text-dark fs-6 mb-0">
                                20/06/2025 &mdash; Sunday
                              </p>
                              <h5 className="text-black lh-1 mt-0 mb-1">
                                World Environment Day
                              </h5>
                              <p className="text-dark fs-6 opacity-75 mb-0">
                                Event
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-light-purple p-4 rounded-4 mt-3">
                          <div className="d-flex justify-content-end">
                            <img
                              src="/assets/images/website.png"
                              width="130"
                              className="position-absolute"
                            />
                          </div>
                          <div>
                            <p className="text-light fs-6 opacity-75 mb-0">
                              Acharyakulam
                            </p>
                            <h5 className="text-white fs-5 fw-medium mb-0 lh-sm">
                              Access the Official Website
                            </h5>
                          </div>

                          <a
                            href="https://www.acharyakulamranchi.com/"
                            target="_blank"
                            class="btn btn-white btn-sm rounded-3 px-4 mt-3 fw-normal"
                          >
                            Visit Website
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default Home;
